import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { useTheme } from '../../context/ThemeContext';
import { ExternalLink, Link2, Clock, X } from 'lucide-react';
import { buildLessonspaceRecordingUrl } from '../../lib/lessonspace';

const buildLessonspaceUrl = (entry) => buildLessonspaceRecordingUrl({
  recordingLink: entry.lessonspaceRecordingLink,
  lessonspaceSessionId: entry.lessonspaceSessionId,
  sessionExternalId: entry.sessionExternalId,
  timestampSeconds: entry.timestampSeconds,
});

const buildLessonUrl = (entry) => {
  const params = new URLSearchParams();
  params.set('tab', 'transcript');
  // WHY: LessonDetail uses `t` as a transcript-text search input; passing the
  // timestamp_display (e.g. "12:34") jumps the user to that segment because
  // transcript rows render the mm:ss label inline.
  if (entry.timestampDisplay) params.set('t', entry.timestampDisplay);
  return `/lessons/${entry.sessionId}?${params.toString()}`;
};

const SignalsDialog = ({ open, onOpenChange, title, icon: Icon, accentClass, entries, kind }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const handleOpenLesson = (entry) => {
    onOpenChange(false);
    navigate(buildLessonUrl(entry));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden ${
          isDark ? 'bg-[#0a0a0a] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'
        }`}
      >
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <div className="flex items-center gap-2.5">
            {Icon && <Icon className={`h-5 w-5 ${accentClass || ''}`} strokeWidth={1.5} />}
            <DialogTitle className={`text-base font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {title}
            </DialogTitle>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{entries.length} total</span>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className={`p-1.5 rounded-md ${isDark ? 'hover:bg-[#18181b] text-[#a1a1aa]' : 'hover:bg-[#f0f0f0] text-[#666]'}`}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {entries.length === 0 ? (
            <div className={`text-center text-sm py-16 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              No entries recorded.
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry, i) => {
                const lessonspaceUrl = buildLessonspaceUrl(entry);
                const primary = entry.quote;
                const secondary = kind === 'review' ? entry.reason : entry.description;
                return (
                  <div
                    key={i}
                    className={`rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <div className={`flex items-center gap-3 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                        <span className="font-medium">
                          {entry.date ? new Date(entry.date).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '--'}
                        </span>
                        <span className={isDark ? 'text-[#52525b]' : 'text-[#bbb]'}>•</span>
                        <span>{entry.tutor || 'Unknown tutor'}</span>
                        {entry.timestampDisplay && (
                          <>
                            <span className={isDark ? 'text-[#52525b]' : 'text-[#bbb]'}>•</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'}`}>
                              <Clock className="h-3 w-3" /> {entry.timestampDisplay}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenLesson(entry)}
                          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                            isDark ? 'bg-[#18181b] hover:bg-[#27272a] text-white' : 'bg-[#f5f5f5] hover:bg-[#e5e5e5] text-[#1a1a1a]'
                          }`}
                        >
                          <Link2 className="h-3 w-3" /> Open lesson
                        </button>
                        {lessonspaceUrl && (
                          <a
                            href={lessonspaceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                              isDark ? 'bg-[#18181b] hover:bg-[#27272a] text-white' : 'bg-[#f5f5f5] hover:bg-[#e5e5e5] text-[#1a1a1a]'
                            }`}
                          >
                            <ExternalLink className="h-3 w-3" /> Open in Lessonspace
                          </a>
                        )}
                      </div>
                    </div>

                    {primary ? (
                      <blockquote
                        className={`border-l-2 pl-4 py-1 italic text-base leading-relaxed ${
                          isDark ? 'border-[#3f3f46] text-[#e4e4e7]' : 'border-[#d4d4d8] text-[#27272a]'
                        }`}
                      >
                        "{primary}"
                      </blockquote>
                    ) : (
                      <p className={`text-xs italic ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        No verbatim quote captured for this entry.
                      </p>
                    )}

                    {secondary && (
                      <p className={`mt-3 text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>
                        {secondary}
                      </p>
                    )}

                    {kind === 'review' && (entry.contextBefore || entry.contextAfter) && (
                      <div className={`mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs ${isDark ? 'text-[#71717a]' : 'text-[#888]'}`}>
                        {entry.contextBefore && (
                          <div>
                            <span className={`uppercase tracking-wider text-[10px] font-medium ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Before</span>
                            <p className="mt-1">{entry.contextBefore}</p>
                          </div>
                        )}
                        {entry.contextAfter && (
                          <div>
                            <span className={`uppercase tracking-wider text-[10px] font-medium ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>After</span>
                            <p className="mt-1">{entry.contextAfter}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignalsDialog;
